// src/utils/logger.ts
// Logger condicional central do frontend.
//
// Em DESENVOLVIMENTO (`import.meta.env.DEV`) espelha o `console` nativo — DX
// preservada, call site original mantido no DevTools (via `.bind(console)`).
// Em PRODUÇÃO é **no-op** em todos os níveis, inclusive `error`.
//
// Por quê:
//  - §E: não vazar dado de negócio no console do cliente (DevTools, screen
//    share, suporte remoto, máquina compartilhada).
//  - ADR-008: NÃO roteamos os erros tratados ao Sentry em massa — o gatilho
//    (b) ">50 erros/dia" está ligado à decisão de residency EU/LGPD. Erros de
//    runtime relevantes continuam capturados como `unhandled` pelo Sentry
//    (`sentry.ts`, `beforeSend` mascara PII). Roteamento seletivo ao Sentry é
//    decisão futura consciente (ver ADR de política de logging em decisions.md).
//
// Uso: `import { logger } from '@/utils/logger'` e trocar `console.x` por
// `logger.x`. A regra ESLint `no-console` proíbe `console.*` fora deste arquivo.

type LogFn = (...args: unknown[]) => void;
export type Logger = Record<'debug' | 'info' | 'log' | 'warn' | 'error', LogFn>;

const noop: LogFn = () => {};

// Fábrica pura (testável): `isDev=false` → no-op em todos os níveis; `isDev=true`
// → espelha o console nativo preservando o call site no DevTools (`.bind`).
export function createLogger(isDev: boolean): Logger {
  if (!isDev) {
    return { debug: noop, info: noop, log: noop, warn: noop, error: noop };
  }
  return {
    debug: console.debug.bind(console),
    info: console.info.bind(console),
    log: console.log.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
  };
}

// `import.meta.env` só existe sob o Vite; `?.` protege runners que não o injetam
// (ex.: Playwright/Node nos testes unitários), caindo com segurança em no-op.
export const logger: Logger = createLogger(import.meta.env?.DEV ?? false);
